import { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import * as Sentry from '@sentry/react';
import { useOrganizationContext } from './OrganizationContext';
import { AdminProviderProps, Company, CompaniesContextType } from '../services/types';

// Configure axios to retry failed requests
axiosRetry(axios, {
    retries: 3,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => axiosRetry.isNetworkOrIdempotentRequestError(error),
});

// Initial/default context value
const CompaniesContext = createContext<CompaniesContextType>({
    companies: [],
    fetchCompanies: () => {},
    loading: false,
});

export const CompaniesProvider = ({ children }: AdminProviderProps) => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [loading, setLoading] = useState(false);
    const { partnerId } = useOrganizationContext();
    const apiUrl = `${import.meta.env.VITE_COMMERCE_BACKEND_URL}/api/getcompanies`;

    const fetchCompanies = async () => {
        setLoading(true);

        if (partnerId === -1 || !partnerId) {
            console.log('Skipping companies fetch for partnerId -1 (Admin)');
            setLoading(false);
            return;
        }

        try {
            const response = await axios.get(apiUrl, { params: { partnerId } });

            if (response.status === 200) {
                const data = response.data.data;
                setCompanies(data);
            } else {
                console.error('Error fetching companies:', response.data);
                setLoading(false);
            }
        } catch (error) {
            if (axios.isAxiosError(error)) {
                if (error.code === 'ECONNABORTED') {
                    Sentry.captureMessage('Request Timeout: Companies request took too long', 'warning');
                } else if (error.message.includes('Network Error')) {
                    Sentry.captureMessage('Network Error: Unable to reach companies endpoint', 'error');
                }

                Sentry.withScope(scope => {
                    scope.setContext('Axios Request', { url: apiUrl });
                    scope.setTag('error_code', error.code || 'UNKNOWN');
                    Sentry.captureException(error);
                });

                console.error('Error fetching companies:', error);
            } else {
                Sentry.captureException(error);
                console.error('Unexpected error fetching companies:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, [partnerId]);

    return (
        <CompaniesContext.Provider value={{ companies, fetchCompanies, loading }}>
            {children}
        </CompaniesContext.Provider>
    );
};

export default CompaniesContext;
